// src/shared/streaming/TelemetryCodec.ts
// ────────────────────────────────────────────────────────────────────────────
// Binary wire codec for time-series telemetry data.
//
// Wire format (per frame):
//   [1 byte  metric_id]
//   [4 bytes uint32-BE  point_count]
//   [N × 16 bytes: float64-BE timestamp_ms  +  float64-BE value]
//
// A stream consists of one or more concatenated frames.
// Compact: 17 bytes/point overhead vs ~40 bytes for JSON.
//
// All metric IDs, keys, and reverse maps are now derived from the
// Signal Family Registry (SignalRegistry.ts) — single source of truth.
// ────────────────────────────────────────────────────────────────────────────

import {
  METRIC_ID as _METRIC_ID,
  ID_TO_KEY as _ID_TO_KEY,
  type MetricKey as _MetricKey,
} from './SignalRegistry';

/**
 * Canonical metric IDs for the wire format — derived from SignalRegistry.
 */
export const METRIC_ID = _METRIC_ID;

export type MetricKey = _MetricKey;

/** Reverse map: wire id → metric key. */
const ID_TO_KEY = _ID_TO_KEY;

export interface TimeSeriesPoint {
  ts:    number; // epoch-ms
  value: number;
}

/** One decoded frame from the binary stream. */
export interface DecodedFrame {
  metric: MetricKey;
  points: TimeSeriesPoint[];
}

// ── Wire constants ───────────────────────────────────────────────────────
const FRAME_HEADER = 5;   // 1 metric_id + 4 point_count
const POINT_SIZE   = 16;  // 8 ts + 8 value

// Max sane point count per frame — 500K points = 8 MB per frame.
// Protects against corrupt wire data causing decoder overflow.
const MAX_POINTS_PER_FRAME = 500_000;

/** Mux scope-block marker byte (must match bin-stream.mjs). */
export const MUX_SCOPE_MARKER = 0xFE;

// ── Encode ───────────────────────────────────────────────────────────────

/**
 * Encode a set of metric → points into a single binary buffer.
 * Returns an ArrayBuffer ready for streaming or direct transfer.
 */
export function encodeFrames(
  metrics: Partial<Record<MetricKey, TimeSeriesPoint[]>>,
): ArrayBuffer {
  let totalBytes = 0;
  const entries: [number, TimeSeriesPoint[]][] = [];

  for (const [key, pts] of Object.entries(metrics) as [MetricKey, TimeSeriesPoint[]][]) {
    const mid = METRIC_ID[key];
    if (mid === undefined || !pts || pts.length === 0) continue;
    entries.push([mid, pts]);
    totalBytes += FRAME_HEADER + pts.length * POINT_SIZE;
  }

  const buf = new ArrayBuffer(totalBytes);
  const view = new DataView(buf);
  let offset = 0;

  for (const [mid, pts] of entries) {
    view.setUint8(offset, mid);     offset += 1;
    view.setUint32(offset, pts.length, false); offset += 4;  // big-endian
    for (const p of pts) {
      view.setFloat64(offset, p.ts,    false); offset += 8;
      view.setFloat64(offset, p.value, false); offset += 8;
    }
  }

  return buf;
}

// ── Decode ───────────────────────────────────────────────────────────────

/**
 * Decode one or more frames from a binary buffer (or partial buffer).
 * Returns decoded frames + the number of bytes consumed.
 * Unconsumed trailing bytes indicate an incomplete frame — caller should
 * buffer them and prepend to the next chunk.
 */
export function decodeFrames(
  buffer: ArrayBuffer,
  byteOffset = 0,
  byteLength?: number,
): { frames: DecodedFrame[]; consumed: number } {
  const len = byteLength ?? buffer.byteLength - byteOffset;
  const view = new DataView(buffer, byteOffset, len);
  const frames: DecodedFrame[] = [];
  let pos = 0;

  while (pos + FRAME_HEADER <= len) {
    const mid   = view.getUint8(pos);
    const count = view.getUint32(pos + 1, false);
    if (count > MAX_POINTS_PER_FRAME) break; // corrupt count — abort decode
    const need  = FRAME_HEADER + count * POINT_SIZE;
    if (pos + need > len) break; // incomplete frame — wait for more data

    const metric = ID_TO_KEY[mid];
    if (!metric) { pos += need; continue; } // skip unknown metrics

    const points: TimeSeriesPoint[] = new Array(count);
    let pOff = pos + FRAME_HEADER;
    for (let i = 0; i < count; i++) {
      points[i] = {
        ts:    view.getFloat64(pOff,     false),
        value: view.getFloat64(pOff + 8, false),
      };
      pOff += POINT_SIZE;
    }

    frames.push({ metric, points });
    pos += need;
  }

  return { frames, consumed: pos };
}

// ── Mux Decode ───────────────────────────────────────────────────────────

/** One scope block from the multiplexed binary stream. */
export interface MuxScopeBlock {
  scopeKey: string;
  frames:   DecodedFrame[];
}

/**
 * Decode a multiplexed binary buffer containing multiple scope blocks.
 *
 * Mux wire format:
 *   [0xFE][1B key_len][key UTF-8]       — scope header
 *   [metric frames...]                  — standard metric frames until next 0xFE or EOF
 *
 * Returns all scope blocks and number of bytes consumed.
 * Supports partial/chunked buffers — unconsumed bytes should be buffered.
 */
export function decodeMuxFrames(
  buffer: ArrayBuffer,
  byteOffset = 0,
  byteLength?: number,
): { blocks: MuxScopeBlock[]; consumed: number } {
  const len = byteLength ?? buffer.byteLength - byteOffset;
  const view = new DataView(buffer, byteOffset, len);
  const blocks: MuxScopeBlock[] = [];
  let pos = 0;

  while (pos < len) {
    // Must start with scope header marker
    if (view.getUint8(pos) !== MUX_SCOPE_MARKER) break;
    if (pos + 2 > len) break; // need marker + key_len

    const keyLen = view.getUint8(pos + 1);
    if (pos + 2 + keyLen > len) break; // incomplete scope header

    const keyBytes = new Uint8Array(buffer, byteOffset + pos + 2, keyLen);
    const scopeKey = new TextDecoder().decode(keyBytes);
    pos += 2 + keyLen;

    // Decode metric frames until we hit next 0xFE marker or end of buffer
    const frames: DecodedFrame[] = [];
    while (pos < len) {
      // Peek: is next byte a scope marker?
      if (view.getUint8(pos) === MUX_SCOPE_MARKER) break;

      // Try to decode a standard metric frame
      if (pos + FRAME_HEADER > len) break;
      const mid   = view.getUint8(pos);
      const count = view.getUint32(pos + 1, false);
      if (count > MAX_POINTS_PER_FRAME) break; // corrupt count — abort scope
      const need  = FRAME_HEADER + count * POINT_SIZE;
      if (pos + need > len) break; // incomplete frame

      const metric = ID_TO_KEY[mid];
      if (!metric) { pos += need; continue; }

      const points: TimeSeriesPoint[] = new Array(count);
      let pOff = pos + FRAME_HEADER;
      for (let i = 0; i < count; i++) {
        points[i] = {
          ts:    view.getFloat64(pOff,     false),
          value: view.getFloat64(pOff + 8, false),
        };
        pOff += POINT_SIZE;
      }

      frames.push({ metric, points });
      pos += need;
    }

    blocks.push({ scopeKey, frames });
  }

  return { blocks, consumed: pos };
}
